/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react-hooks/rules-of-hooks */
import React, {
  createContext,
  Dispatch,
  ReactNode,
  useCallback,
  useContext,
  useReducer,
  useRef,
} from 'react';
import { __rest } from 'tslib';

import config from '../../../config';
import { useOrgStore } from '@Models/useOrgModel';
import {
  AllFacets,
  Criterion,
  QuarantineLogTypeEnum,
  SearchItem,
  SortInterface,
  TimeRangeInterface,
} from '@Types/log';
import { escapeQueryString, surroundWildcardForAscii } from '@Utils/chart';
import { convertLocalTimeToUtcTimeStr } from '@Utils/time';
import {
  isoTimeFormat,
  mappingFields,
  quishingMappingFields,
  timeFields,
} from '@Log/constants';
import {
  getQuarantineL10nObj,
  getTimeRange,
  LogTimeUnit,
  unwrapSearchFieldFuncMap,
} from '@Log/shared';
import { quarantineConfig } from '@Quarantine/utils/constants';
import { QuarantineLogItem, QuarantineLogState } from '@Quarantine/utils/types';
import { MappingField, TimeField } from '../../Log/type';
import searchFields from 'src/mapping/log.json';

const logSearchFields = searchFields;
const enum QuarantineActionType {
  SetQuarantineType = 'setQuarantineType',
  SetOrgId = 'setOrgId',
  SetLogsAndFacets = 'setLogsAndFacets',
  SetFacets = 'setFacets',
  SetTimeRange = 'setTimeRange',
  SetTrigger = 'setTrigger',
  SetCriteria = 'setCriteria',
  SetPageStart = 'setPageStart',
  SetQueryParam = 'setQueryParam',
  SetSort = 'setSort',
}

const useEnhancedReducer = (
  reducer: Parameters<typeof useReducer>[0],
  initState: Parameters<typeof useReducer>[1],
  initializer?: Parameters<typeof useReducer>[2]
): [
  Parameters<typeof useReducer>[1],
  Dispatch<{
    type: QuarantineActionType;
    payload: Partial<QuarantineLogState>;
  }>,
  () => QuarantineLogState,
] => {
  const latestState = useRef<ReturnType<typeof reducer>>(initState);
  const getState = useCallback(() => latestState.current, []);
  return [
    ...useReducer(
      (
        state: Parameters<typeof reducer>[0],
        action: Parameters<typeof reducer>[1]
      ) => (latestState.current = reducer(state, action)),
      initState,
      initializer
    ),
    getState,
  ];
};

const useQuarantineLogContext = (initState: QuarantineLogState) => {
  const reducer = (
    preState: QuarantineLogState,
    action: {
      type: QuarantineActionType;
      payload: Partial<QuarantineLogState>;
    }
  ): QuarantineLogState => {
    const { type, payload } = action;
    switch (type) {
      case QuarantineActionType.SetQuarantineType:
      case QuarantineActionType.SetOrgId:
      case QuarantineActionType.SetLogsAndFacets:
      case QuarantineActionType.SetFacets:
      case QuarantineActionType.SetTimeRange:
      case QuarantineActionType.SetTrigger:
      case QuarantineActionType.SetCriteria:
      case QuarantineActionType.SetPageStart:
      case QuarantineActionType.SetQueryParam:
      case QuarantineActionType.SetSort:
        return {
          ...preState,
          ...payload,
        };
      default:
        return preState;
    }
  };
  const [state, dispatch, getState] = useEnhancedReducer(reducer, initState);
  const { orgs: orgsArr } = useOrgStore();

  //1. menu toggle QuarantineType 2. org switch
  const setQuarantineType = useCallback(
    (quarantineType: QuarantineLogTypeEnum) => {
      const includeProps = ['quarantineType'];
      if (getState().quarantineType === quarantineType) {
        includeProps.push('columnConfig');
      }
      const initState = __rest(
        config.ui.quarantineDefaultValue.quarantineLogState,
        ['quarantineType']
      );
      dispatch({
        type: QuarantineActionType.SetQuarantineType,
        payload: {
          ...initState, //init state
          quarantineType,
          timeRange: getTimeRange(LogTimeUnit.Last24h, quarantineType),
          trigger: new Date(),
        },
      });
    },
    []
  );
  const setOrgId = useCallback(
    (orgId: QuarantineLogTypeEnum, quarantineType?: QuarantineLogTypeEnum) => {
      //set org的时候 ， 很可能也要设置type ，如果type不变， 忽略config ， 如果变了， 初始化config
      // const includeProps = [];
      // const updateState: { [k: string]: any } = {};
      const initState = __rest(
        config.ui.quarantineDefaultValue.quarantineLogState,
        ['quarantineType']
      );
      dispatch({
        type: QuarantineActionType.SetQuarantineType,
        payload: {
          ...initState, //当重置org的时候，还需要考虑，当前org有没有
          quarantineType,
        },
      });
      const curState = getState();
      console.log(curState);
    },
    []
  );
  const setLogs = useCallback((logs: QuarantineLogItem[], total: number) => {
    dispatch({
      type: QuarantineActionType.SetLogsAndFacets,
      payload: {
        total,
        logs,
      },
    });
  }, []);
  const setAllFacets = useCallback((facets: AllFacets) => {
    dispatch({
      type: QuarantineActionType.SetLogsAndFacets,
      payload: {
        allFacets: facets,
      },
    });
  }, []);
  const setLogsAndFacets = useCallback(
    (logs: QuarantineLogItem[], total: number, facets: AllFacets) => {
      dispatch({
        type: QuarantineActionType.SetLogsAndFacets,
        payload: {
          total,
          logs,
          allFacets: facets,
        },
      });
    },
    []
  );
  const setFacets = useCallback((facets: SearchItem[]) => {
    dispatch({
      type: QuarantineActionType.SetFacets,
      payload: {
        page: {
          start: 1,
          size: 100,
        },
        facets,
        trigger: new Date(),
      },
    });
  }, []);
  const setTimeRange = useCallback((timeRange: TimeRangeInterface) => {
    dispatch({
      type: QuarantineActionType.SetTimeRange,
      payload: {
        timeRange,
      },
    });
  }, []);
  //refresh table data
  const setTrigger = useCallback(() => {
    dispatch({
      type: QuarantineActionType.SetTrigger,
      payload: {
        page: {
          start: 1,
          size: 100,
        },
        trigger: new Date(),
      },
    });
  }, []);
  const setCriteria = useCallback((criteria: Criterion[]) => {
    dispatch({
      type: QuarantineActionType.SetCriteria,
      payload: {
        criteria,
      },
    });
  }, []);
  const setPageStart = useCallback((start: number) => {
    dispatch({
      type: QuarantineActionType.SetPageStart,
      payload: {
        page: {
          start,
          size: 100,
        },
      },
    });
  }, []);
  const convertCriteriaToSearchItems = (
    initState: QuarantineLogState
  ): SearchItem[] => {
    const { criteria, quarantineType } = initState;
    const quarantineL10nMap = getQuarantineL10nObj();
    const wrapFields = quarantineConfig[quarantineType].wrapFields;
    const resultItems: SearchItem[] = [];
    const fieldValueMap: { [key: string]: string[] } = {};
    criteria.forEach((criterion) => {
      const { field, value } = criterion;
      let tempValues: string[] = [];
      // process map quishing, quishingMappingFields only support full match search and only support value 'QUISHING'
      if (quishingMappingFields.includes(field) && value === 'QUISHING') {
        tempValues.push('VAN_QUISHING.UMXX');
      }
      // process mergedFields
      const mergedFields = quarantineConfig[quarantineType].mergedFields[field];
      if (Array.isArray(mergedFields) && mergedFields.length) {
        mergedFields.forEach((mergedField) => {
          // process map Fields
          if (mappingFields.includes(mergedField as MappingField)) {
            Object.keys(quarantineL10nMap[mergedField]).forEach((key) => {
              if (
                quarantineL10nMap[mergedField][key].toLowerCase() ===
                value.toLowerCase()
              ) {
                tempValues.push(key);
              }
            });
          }
          // process wrap Fields
          if (
            Object.keys(unwrapSearchFieldFuncMap).includes(mergedField) &&
            wrapFields.includes(field)
          ) {
            const wrapValues =
              unwrapSearchFieldFuncMap[
                mergedField as keyof typeof unwrapSearchFieldFuncMap
              ](value);
            tempValues = Array.from(new Set([...tempValues, ...wrapValues]));
          }
        });
      }
      if (timeFields.includes(field as TimeField)) {
        // process timeFields
        // convert value to date format
        const utcTime = convertLocalTimeToUtcTimeStr(value, isoTimeFormat);
        tempValues.push(utcTime);
        tempValues.push(utcTime);
      } else if (
        Object.keys(unwrapSearchFieldFuncMap).includes(field) &&
        wrapFields.includes(field)
      ) {
        // process unwrap search fields
        const wrapValues =
          unwrapSearchFieldFuncMap[
            field as keyof typeof unwrapSearchFieldFuncMap
          ](value);
        tempValues = Array.from(new Set([...tempValues, ...wrapValues]));
      } else if (mappingFields.includes(field as MappingField)) {
        // process mappingFields
        Object.keys(quarantineL10nMap[field]).forEach((key) => {
          if (
            quarantineL10nMap[field][key].toLowerCase() === value.toLowerCase()
          ) {
            tempValues.push(key);
          }
        });
      } else if (field === 'all') {
        Object.values(quarantineL10nMap).forEach((v) => {
          if (typeof v === 'string') {
            if (v.toLowerCase() === value.toLowerCase()) {
              tempValues.push(v);
            }
          } else {
            Object.entries(v).forEach(([key, val]) => {
              if (val && val.toLocaleString() === value.toLocaleString()) {
                tempValues.push(key);
              }
            });
          }
        });
      }

      // process query string, escape value
      if (!timeFields.includes(field as TimeField)) {
        if (field === 'companyGuid') {
          // process orgName
          const orgs = orgsArr
            ? orgsArr.filter(
                (org) => org.value.toLowerCase() === value.toLowerCase()
              )
            : [];
          if (orgs.length) {
            orgs.forEach((org) => (tempValues = tempValues.concat(org.key)));
          } else {
            tempValues = tempValues.concat('undefined');
          }
        } else if (field === 'mailDirection') {
          // process mailDirection
          if (
            value.toLowerCase() ===
            quarantineL10nMap['mailDirection']['in'].toLowerCase()
          ) {
            tempValues = tempValues.concat('in');
          } else if (
            value.toLowerCase() ===
            quarantineL10nMap['mailDirection']['out'].toLowerCase()
          ) {
            tempValues = tempValues.concat('out');
          }
        } else {
          // process common fields
          tempValues = tempValues.concat(value);
        }
      }

      // merge values
      // if criterion filed is same, combine the values
      if (fieldValueMap[field]) {
        const values = fieldValueMap[field];
        // remove the duplicate element in values array
        fieldValueMap[field] = Array.from(new Set([...values, ...tempValues]));
      } else {
        fieldValueMap[field] = Array.from(new Set([...tempValues]));
      }
    });
    //convert fieldValueMap to searchItem
    Object.keys(fieldValueMap).forEach((field) => {
      let searchType = 'queryString';
      let searchValues = fieldValueMap[field];
      const fieldItem = logSearchFields.logSearchFields['quarantine'].find(
        (item) => item.field === field
      );
      if (fieldItem) {
        searchType = fieldItem.type;
      }
      if (field === 'all') {
        searchType = 'terms';
      }
      const escapeValues: string[] = [];
      if (searchType === 'queryString') {
        fieldValueMap[field].forEach((value) => {
          escapeValues.push(
            ...surroundWildcardForAscii(escapeQueryString(field, value), field)
          );
        });
        searchValues = escapeValues;
      }
      if (timeFields.includes(field as TimeField)) {
        searchType = 'range';
      }
      const item: SearchItem = {
        field,
        type: searchType,
        values: searchValues,
      };
      resultItems.push(item);
    });
    return resultItems;
  };
  const setQueryParams = useCallback(
    (
      quarantineType: QuarantineLogTypeEnum,
      facets: SearchItem[],
      timeRange: TimeRangeInterface
    ) => {
      const initState = __rest(
        config.ui.quarantineDefaultValue.quarantineLogState,
        ['quarantineType']
      );
      dispatch({
        type: QuarantineActionType.SetQueryParam,
        payload: {
          ...initState,
          quarantineType,
          timeRange,
          page: {
            start: 1,
            size: 100,
          },
          facets,
          trigger: new Date(),
        },
      });
    },
    []
  );
  const setSort = useCallback((sort: SortInterface) => {
    dispatch({
      type: QuarantineActionType.SetSort,
      payload: {
        page: {
          start: 1,
          size: 100,
        },
        sort,
        trigger: new Date(),
      },
    });
  }, []);
  return {
    state,
    getState,
    setQuarantineType,
    setOrgId,
    setLogsAndFacets,
    setLogs,
    setAllFacets,
    setFacets,
    setTimeRange,
    setTrigger,
    setCriteria,
    setPageStart,
    setQueryParams,
    setSort,
    convertCriteriaToSearchItems,
  } as {
    state: QuarantineLogState;
    getState: Function;
    setQuarantineType: typeof setQuarantineType;
    setOrgId: typeof setOrgId;
    setLogsAndFacets: typeof setLogsAndFacets;
    setAllFacets: typeof setAllFacets;
    setLogs: typeof setLogs;
    setFacets: typeof setFacets;
    setTimeRange: typeof setTimeRange;
    setTrigger: typeof setTrigger;
    setCriteria: typeof setCriteria;
    setPageStart: typeof setPageStart;
    setQueryParams: typeof setQueryParams;
    setSort: typeof setSort;
    convertCriteriaToSearchItems: typeof convertCriteriaToSearchItems;
  };
};

type UseQuarantineContextType = ReturnType<typeof useQuarantineLogContext>;

const QuarantineLogContext = createContext<UseQuarantineContextType>({
  state: {} as QuarantineLogState,
  getState: () => {},
  setQuarantineType: () => {},
  setOrgId: () => {},
  setLogsAndFacets: () => {},
  setAllFacets: () => {},
  setLogs: () => {},
  setFacets: () => {},
  setTimeRange: () => {},
  setTrigger: () => {},
  setCriteria: () => {},
  setPageStart: () => {},
  setQueryParams: () => {},
  setSort: () => {},
  convertCriteriaToSearchItems: () => [],
});

export const QuarantineLogContextProvider: React.FC<{ children: ReactNode }> = (
  props
) => {
  const initState = config.ui.quarantineDefaultValue.quarantineLogState;
  return (
    <QuarantineLogContext.Provider
      value={useQuarantineLogContext({ ...initState })}
    >
      {props.children}
    </QuarantineLogContext.Provider>
  );
};

export const useQuarantineReducerContext = () => {
  return useContext(QuarantineLogContext);
};
